import { useCallback, useMemo } from 'react';

import { queryString } from '@shared/hooks/recoil-query';
import { useCachedResource } from '@shared/hooks/useCachedResource';
import { api_ } from '@shared/plugIn/axios';
import { kstDayEndISO, kstDayStartISO, kstShiftDay } from '@shared/utils/kstTime.util';

import { BugoStatFevent } from './adminBugoStat.types';
import { OrderStatKind } from './adminOrderStat.types';
import { ORDER_FETCH_LIMIT, useAdminOrderStat } from './useAdminOrderStat';
import { useAnalyticsBrandNames } from './useAnalyticsBrandNames';

/** 발인 상가 조회 상한 — 넘치면 "일부만 집계" 경고 */
export const PENETRATION_FEVENT_LIMIT = 20000;

/**
 * 발인 기간 앞뒤로 주문을 얼마나 더 끌어올지.
 *
 * 발인일 기준으로 묶으려면 주문을 판매일 범위대로 끊어 오면 안 된다 —
 * 8월 발인 상가의 화환이 7월 말에 결제되고, 답례품은 9월에 주문되기 때문.
 * 창을 넓게 잡고 상가(fevent) 쪽에서 발인일로 거른다.
 */
export const ORDER_WINDOW_DAYS: Record<OrderStatKind, { lead: number; tail: number }> = {
  //* 화환은 조문 시점(대개 발인 1~3일 전)에 결제된다. 발인 뒤 결제는 드물다
  flower: { lead: 14, tail: 10 },
  //* 답례품 스토어는 발인 +30일까지 열려 있다 — 뒤쪽을 넉넉히 본다
  gift: { lead: 14, tail: 45 },
};

/** 발인 기간 → 실제로 훑을 주문 기간 */
export const orderWindowOf = (
  kind: OrderStatKind,
  from: string,
  to: string,
): { from: string; to: string } => {
  if (!from || !to) return { from: '', to: '' };
  const { lead, tail } = ORDER_WINDOW_DAYS[kind];
  return { from: kstShiftDay(from, -lead), to: kstShiftDay(to, tail) };
};

//* 침투율에 필요한 최소 필드만. 조의글·부고(virtual populate)는 여기선 안 쓴다 —
//* 구서버 StrictPopulate 위험을 굳이 떠안을 이유가 없다.
const FEVENT_SELECT = [
  'bugoBrand',
  'bugoBrandAgencyIndex',
  'meta.shareCount',
  'deceasedInfo.name',
  'deceasedInfo.coffinOut.date',
  'funeralHomeInfo',
  'funeralHomeInfoEmbed.name',
  'roomInfoEmbed.name',
].join(' ');

//* 상조명은 이름 맵(`useAnalyticsBrandNames`)으로 붙인다 — 맵을 못 받았을 때만 폴백 populate
const BRAND_POPULATE = {
  path: 'bugoBrand',
  select:
    'type bugoAgency.corpName bugoAgency.nickName bugoAgencyList.corpName bugoAgencyList.nickName',
};

const FUNERAL_HOME_POPULATE = { path: 'funeralHomeInfo', select: 'name' };

const buildFeventUrl = (from: string, to: string, brandPopulate: boolean): string => {
  const query = queryString({
    filter: {
      //* 발인일 기준. isDeleted는 절대 필터하지 않는다 (과거 기간이 통째로 비어 보인다)
      'deceasedInfo.coffinOut.date': {
        $gte: kstDayStartISO(from),
        $lte: kstDayEndISO(to),
      },
    },
    populate: brandPopulate
      ? [BRAND_POPULATE, FUNERAL_HOME_POPULATE]
      : [FUNERAL_HOME_POPULATE],
    options: {
      limit: PENETRATION_FEVENT_LIMIT,
      sort: { 'deceasedInfo.coffinOut.date': 1 },
    },
  });
  //* queryString은 encode:false라 공백이 든 select를 직접 인코딩해야 URL이 안 깨진다
  return `admin/fevent?${query}&select=${encodeURIComponent(FEVENT_SELECT)}`;
};

/**
 * 서버 message를 살린 오류 — 화면 문구는 여기서 확정하고 `response`는 그대로 얹어
 * 캐시 훅의 404 판별이 계속 동작하게 한다.
 */
const toStatError = (err: unknown): Error => {
  const res = (err as { response?: { status?: number; data?: { message?: string } } })
    ?.response;
  //* 백엔드 전역 필터가 error 코드를 덮어써서 400에서만 서버 message가 의미 있다
  const message =
    res?.status === 400 && res.data?.message
      ? res.data.message
      : res?.status === 401 || res?.status === 403
      ? '조회 권한이 없습니다.'
      : '통계를 불러오지 못했습니다.';
  return Object.assign(new Error(message), { response: res });
};

interface FeventPayload {
  fevents: BugoStatFevent[];
  truncated: boolean;
}

const EMPTY_FEVENTS: BugoStatFevent[] = [];

/**
 * 발인일 기준 상가 경량 조회 (침투율 분모).
 * 상한 2만 건이라 캐시를 태운다 — 구매율 뷰는 한 번 열면 계속 마운트 상태로 남아서
 * 기간을 옮길 때마다 이 조회가 주문 조회와 **함께** 나간다.
 */
const useCoffinOutFevents = (from: string, to: string) => {
  //* 이름 맵이 정해져야 brand populate를 붙일지 말지가 정해진다 (세션 캐시라 보통 즉시)
  const { status: brandStatus, brandPopulateFallback } = useAnalyticsBrandNames();

  const enabled = !!from && !!to && from <= to && brandStatus !== 'loading';
  const key = `adminPenetrationFevent|${from}|${to}|${
    brandPopulateFallback ? 'pop' : '-'
  }`;

  const { data, loading, error, refetch } = useCachedResource<FeventPayload>(
    key,
    async () => {
      try {
        const { data: rows } = await api_.get<BugoStatFevent[]>(
          buildFeventUrl(from, to, brandPopulateFallback),
        );
        const fevents = Array.isArray(rows) ? rows : [];
        return { fevents, truncated: fevents.length >= PENETRATION_FEVENT_LIMIT };
      } catch (err) {
        throw toStatError(err);
      }
    },
    { enabled, errorMessage: '통계를 불러오지 못했습니다.' },
  );

  return {
    status: (error ? 'error' : loading ? 'loading' : data ? 'success' : 'idle') as
      | 'idle'
      | 'loading'
      | 'success'
      | 'error',
    fevents: data?.fevents ?? EMPTY_FEVENTS,
    truncated: data?.truncated ?? false,
    errorMessage: error,
    refetch,
  };
};

/**
 * 발인일 기준 침투율 원자료 — 상가(분모) + 넓은 창의 주문(분자).
 * 상조별 침투율을 주는 전용 API가 없어(docs/admin-order-stat-spec.md)
 * 두 제네릭 조회를 받아 프론트에서 접는다.
 */
export const useAdminPenetration = (kind: OrderStatKind, from: string, to: string) => {
  const orderRange = useMemo(() => orderWindowOf(kind, from, to), [kind, from, to]);

  const feventState = useCoffinOutFevents(from, to);
  const orderState = useAdminOrderStat(kind, orderRange.from, orderRange.to);
  const { resolveBrandName } = useAnalyticsBrandNames();

  //* 둘 다 와야 %가 나온다 — 하나라도 실패하면 실패, 하나라도 로딩이면 로딩
  const status: 'idle' | 'loading' | 'success' | 'error' =
    feventState.status === 'error' || orderState.status === 'error'
      ? 'error'
      : feventState.status === 'loading' || orderState.status === 'loading'
      ? 'loading'
      : feventState.status === 'success' && orderState.status === 'success'
      ? 'success'
      : 'idle';

  const refetch = useCallback(() => {
    void feventState.refetch();
    void orderState.refetch();
  }, [feventState, orderState]);

  return {
    status,
    fevents: feventState.fevents,
    resolveBrandName,
    orders: orderState.orders,
    /** 상가 또는 주문이 조회 상한에 걸려 잘렸는지 */
    truncated: feventState.truncated || orderState.truncated,
    feventTruncated: feventState.truncated,
    orderTruncated: orderState.truncated,
    orderFetchLimit: ORDER_FETCH_LIMIT,
    feventFetchLimit: PENETRATION_FEVENT_LIMIT,
    errorMessage: feventState.errorMessage ?? orderState.errorMessage,
    /** 실제로 훑은 주문 기간 — 화면에 근거로 보여준다 */
    orderWindow: orderRange,
    refetch,
  };
};
